import { FormEvent, useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useStaffAuth } from '../auth/StaffAuthContext';
import { getDepartments, onboardMyStaffProfile } from '../lib/api';
import { appPathFromReturnTo, errorMessage, normalizeProfileText } from '../lib/utils';
import type { Department } from '../types';
import { useToast } from '../context/ToastContext';

export default function StaffOnboardingPage() {
  const { identity, refreshProfile, signOut } = useStaffAuth();
  const [params] = useSearchParams();
  const returnTo = appPathFromReturnTo(params.get('returnTo'));
  const [fullName, setFullName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => { getDepartments().then(setDepartments).catch((error) => showToast(errorMessage(error), 'error')); }, [showToast]);

  if (identity.loading) return <main className="auth-page"><div className="spinner" /></main>;
  if (!identity.isAuthenticated || !identity.email) return <Navigate to={`/staff-auth?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  if (identity.profileError) return <main className="auth-page"><section className="auth-card">
    <div className="eyebrow">Pendaftaran profil pegawai</div><h1>Profil tidak tersedia</h1>
    <p className="muted">Carian profil gagal, jadi pendaftaran dihentikan buat sementara supaya ralat rangkaian tidak dianggap sebagai profil yang tiada.</p>
    <p className="security-callout compact">{identity.profileError}</p>
    <div className="checkin-actions">
      <button className="button button-primary" onClick={() => void refreshProfile().catch((error) => showToast(errorMessage(error), 'error'))}>Cuba muat semula profil</button>
      <button className="button button-secondary" onClick={() => void signOut().catch((error) => showToast(errorMessage(error), 'error'))}>Log Keluar</button>
    </div>
  </section></main>;
  if (identity.hasProfile) return <Navigate to={returnTo} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const selectedDepartmentId = Number(departmentId);
    if (fullName.trim().length < 2 || !Number.isInteger(selectedDepartmentId) || selectedDepartmentId <= 0) {
      showToast('Lengkapkan nama dan bahagian anda.', 'error');
      return;
    }
    setBusy(true);
    try {
      await onboardMyStaffProfile({ fullName: normalizeProfileText(fullName), departmentId: selectedDepartmentId });
      await refreshProfile();
      showToast('Profil pegawai anda telah disediakan.', 'success');
      navigate(returnTo, { replace: true });
    } catch (error) {
      showToast(errorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };

  return <main className="auth-page"><section className="auth-card auth-card-wide">
    <div className="eyebrow">Pendaftaran profil pegawai</div><h1>Lengkapkan profil anda</h1>
    <p className="muted">Log masuk sebagai {identity.email}. E-mel yang telah disahkan tidak boleh diubah di sini.</p>
    <form className="form-stack" onSubmit={submit}>
      <label className="field"><span>Nama penuh</span><input value={fullName} onChange={(event) => setFullName(event.target.value)} autoComplete="name" maxLength={120} required /></label>
      <label className="field"><span>Bahagian</span><select value={departmentId} onChange={(event) => setDepartmentId(event.target.value)} required><option value="">Pilih bahagian</option>{departments.map((department) => <option key={department.department_id} value={department.department_id}>{department.department_name}</option>)}</select></label>
      <button className="button button-primary button-block" disabled={busy}>{busy ? 'Sedang menyimpan…' : 'Simpan profil pegawai'}</button>
    </form>
  </section></main>;
}
